import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/all";
import { useEffect, useRef, useState } from "react";
import "./hero.css"; // Import the new hero-specific CSS
import VariableProximity from "./VariableProximity";
import FullscreenButton from "./FullscreenButton";

gsap.registerPlugin(ScrollTrigger);

// Video slide component to handle individual video refs properly
const VideoSlide = ({ index, isActive, loading, onVideoLoad, onVideoEnd, getVideoSrc }) => {
  const videoRef = useRef(null);
  
  return (
    <div className="hero-slide">
      <video
        ref={videoRef}
        className="hero-video"
        src={getVideoSrc(index)}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        onLoadedData={() => onVideoLoad(index)}
        onEnded={() => onVideoEnd(index)}
      />
      {/* Fullscreen button for each video */}
      <FullscreenButton 
        videoRef={videoRef}
        isVisible={isActive && !loading}
        className="transition-opacity duration-300"
      />
    </div>
  );
};

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  const [isTextVisible, setIsTextVisible] = useState(true);
  
  const totalVideos = 3;

  // Toggle text visibility
  const toggleTextVisibility = () => {
    const textContainer = document.querySelector('.hero-text-container');
    // const gradientOverlay = document.querySelector('.hero-slide::after');
    if (!textContainer) return;

    if (isTextVisible) {
      // Hide text with animation
      gsap.to(textContainer, {
        opacity: 0,
        y: -20,
        duration: 0.5,
        ease: "power2.out",
        onComplete: () => setIsTextVisible(false)
      });
      // Fade out gradient overlay
      gsap.to('.hero-slide', {
        '--gradient-opacity': 0,
        duration: 0.5,
        ease: "power2.out"
      });
    } else {
      // Show text with animation
      setIsTextVisible(true);
      gsap.fromTo(textContainer, 
        { opacity: 0, y: -20 },
        { 
          opacity: 1, 
          y: 0, 
          duration: 0.5, 
          ease: "power2.out" 
        }
      );
      // Fade in gradient overlay
      gsap.to('.hero-slide', {
        '--gradient-opacity': 1,
        duration: 0.5,
        ease: "power2.out"
      });
    }
  };

  // Text content for each slide
  const slideContent = [
    {
      title: "Lead Enrichment Workflow",
      subtitle: "Transform basic contact data into hyper-personalized marketing campaigns through AI-powered deep research and knowledge base integration."
    },
    {
      title: "Frame Processor Workflow",
      subtitle: "Convert screen recordings into a searchable knowledge base by intelligently extracting and indexing every action, tool, and insight from my digital history."
    },
    {
      title: "Social Media Summarization Workflow",
      subtitle: "Automatically extract actionable strategies and workflows from various social media platforms videos through AI-powered transcription and analysis."
    }
  ];
  const heroRef = useRef(null);
  const sliderRef = useRef(null);
  const autoPlayTimeoutRef = useRef(null);

  // Video URLs
  const getVideoSrc = (index) => {
    return `https://storage.googleapis.com/tll-videos-for-web/Hero-${index + 1}.mp4`;
  };


  // Handle video load
  const handleVideoLoad = (index) => {
    console.log(`Video ${index + 1} loaded`);
    
    // Hide loading screen when first video loads
    if (index === 0) {
      setLoading(false);
    }
  };

  // Handle video end - auto advance to next with loop
  const handleVideoEnd = (index) => {
    if (index === currentIndex) {
      // Clear any existing timeout
      if (autoPlayTimeoutRef.current) {
        clearTimeout(autoPlayTimeoutRef.current);
      }
      
      // Add a small delay before advancing
      autoPlayTimeoutRef.current = setTimeout(() => {
        // Loop to first video if at the end
        const nextIndex = index === totalVideos - 1 ? 0 : index + 1;
        goToSlide(nextIndex);
      }, 1000);
    }
  };

  // Go to specific slide
  const goToSlide = (index) => {
    if (index >= 0 && index < totalVideos) {
      setCurrentIndex(index);
      
      // Smooth scroll to the slide
      if (sliderRef.current) {
        const slideWidth = sliderRef.current.offsetWidth;
        sliderRef.current.scrollTo({
          left: slideWidth * index,
          behavior: 'smooth'
        });
      }
    }
  };

  // Note: Video playback is now handled inside VideoSlide component

  // Handle scroll to update current index
  const handleScroll = () => {
    if (!sliderRef.current || isDragging) return;
    
    // Debounce the scroll handling
    clearTimeout(autoPlayTimeoutRef.current);
    autoPlayTimeoutRef.current = setTimeout(() => {
      const slideWidth = sliderRef.current.offsetWidth;
      const scrollPosition = sliderRef.current.scrollLeft;
      const newIndex = Math.round(scrollPosition / slideWidth);
      
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < totalVideos) {
        setCurrentIndex(newIndex);
      }
    }, 100);
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    e.preventDefault(); // Prevent text selection
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    setDragStartTime(Date.now());
    sliderRef.current.classList.add('dragging');
    heroRef.current.classList.add('dragging');
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 3; // High sensitivity for responsive dragging
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    
    // Calculate drag distance and velocity
    const endX = e.pageX - sliderRef.current.offsetLeft;
    const dragDistance = endX - startX;
    const dragDuration = Date.now() - dragStartTime;
    const velocity = Math.abs(dragDistance) / dragDuration;
    
    // Lower threshold for faster drags
    const threshold = velocity > 0.5 ? 20 : 40;
    
    setIsDragging(false);
    sliderRef.current.classList.remove('dragging');
    heroRef.current.classList.remove('dragging');
    
    // Determine target slide based primarily on drag distance
    setTimeout(() => {
      let targetIndex = currentIndex;
      
      // Check if user intended to change slides based on drag distance
      if (Math.abs(dragDistance) > threshold) {
        if (dragDistance < 0) {
          // Dragged left - go to next slide (with loop)
          targetIndex = currentIndex === totalVideos - 1 ? 0 : currentIndex + 1;
        } else if (dragDistance > 0) {
          // Dragged right - go to previous slide (with loop)
          targetIndex = currentIndex === 0 ? totalVideos - 1 : currentIndex - 1;
        }
      } else {
        // If drag wasn't significant, snap to nearest based on scroll position
        const slideWidth = sliderRef.current.offsetWidth;
        const scrollPosition = sliderRef.current.scrollLeft;
        targetIndex = Math.round(scrollPosition / slideWidth);
      }
      
      goToSlide(targetIndex);
    }, 50);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      sliderRef.current.classList.remove('dragging');
      heroRef.current.classList.remove('dragging');
      
      // Still snap to nearest slide when leaving
      setTimeout(() => {
        const slideWidth = sliderRef.current.offsetWidth;
        const scrollPosition = sliderRef.current.scrollLeft;
        const nearestIndex = Math.round(scrollPosition / slideWidth);
        goToSlide(nearestIndex);
      }, 50);
    }
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.targetTouches[0].clientX);
    setScrollLeft(sliderRef.current.scrollLeft);
    setDragStartTime(Date.now());
    
    // Add dragging class to prevent snap during touch
    sliderRef.current.classList.add('dragging');
    heroRef.current.classList.add('dragging');
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !startX) return;
    
    const currentX = e.targetTouches[0].clientX;
    const diff = startX - currentX;
    
    // Prevent vertical scrolling when swiping horizontally
    if (Math.abs(diff) > 5) {
      e.preventDefault();
      
      // Manually update scroll position during touch
      const walk = diff * 2; // Adjust sensitivity as needed
      sliderRef.current.scrollLeft = scrollLeft + walk;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isDragging || !startX) return;
    
    setIsDragging(false);
    sliderRef.current.classList.remove('dragging');
    heroRef.current.classList.remove('dragging');
    
    const endX = e.changedTouches[0].clientX;
    const diff = startX - endX;
    const dragDuration = Date.now() - dragStartTime;
    const velocity = Math.abs(diff) / dragDuration;
    
    // Lower threshold for mobile - any meaningful swipe should change slides
    const threshold = 30;
    
    // Immediately determine and go to the target slide
    let targetIndex = currentIndex;
    
    if (Math.abs(diff) > threshold || velocity > 0.3) {
      if (diff > 0) {
        // Swiped left - go to next (with loop)
        targetIndex = currentIndex === totalVideos - 1 ? 0 : currentIndex + 1;
      } else {
        // Swiped right - go to previous (with loop)
        targetIndex = currentIndex === 0 ? totalVideos - 1 : currentIndex - 1;
      }
    }
    
    // Immediately snap to the target slide
    goToSlide(targetIndex);
    
    // Reset states
    setStartX(null);
    setScrollLeft(0);
  };

  // Handle keyboard navigation with looping
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        goToSlide(currentIndex === totalVideos - 1 ? 0 : currentIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        goToSlide(currentIndex === 0 ? totalVideos - 1 : currentIndex - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  // Note: Video loading is now handled inside VideoSlide component

  // GSAP animation for entrance
  useGSAP(() => {
    if (!loading) {
      gsap.from(".hero-content > *", {
        y: 50,
        opacity: 0,
        duration: 1,
        stagger: 0.2,
        ease: "power3.out"
      });
    }
  }, [loading]);

  // GSAP animation for text overlay transition
  useGSAP(() => {
    if (!loading) {
      gsap.to(".hero-text-container", {
        opacity: 0,
        duration: 0.15,
        ease: "power2.out",
        onComplete: () => {
          gsap.to(".hero-text-container", {
            opacity: 1,
            duration: 0.15,
            ease: "power2.out"
          });
        }
      });
    }
  }, [currentIndex]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (autoPlayTimeoutRef.current) {
        clearTimeout(autoPlayTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={heroRef} className="hero-container">
      {/* Main page heading for accessibility */}
      <h1 className="sr-only">TheLogicLoom - AI Automation & Workflow Development</h1>
      
      {loading && (
        <div className="hero-loader">
          <div className="three-body">
            <div className="three-body__dot"></div>
            <div className="three-body__dot"></div>
            <div className="three-body__dot"></div>
          </div>
        </div>
      )}

      <div className="hero-wrapper">
        {/* Slider Container */}
        <div 
          ref={sliderRef}
          className="hero-slider"
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {[...Array(totalVideos)].map((_, index) => (
            <VideoSlide
              key={index}
              index={index}
              isActive={index === currentIndex}
              loading={loading}
              onVideoLoad={handleVideoLoad}
              onVideoEnd={handleVideoEnd}
              getVideoSrc={getVideoSrc}
            />
          ))}
        </div>

        {/* Content Overlay */}
        <div ref={heroRef} className="hero-content">
          <div 
            className="hero-text-container hero-text-top cursor-pointer transition-all duration-300 hover:scale-105" 
            style={{ opacity: 1 }}
            onClick={toggleTextVisibility}
            title={isTextVisible ? "Click to hide text" : "Click to show text"}
          >
            <div style={{ marginBottom: '1rem' }}>
              <VariableProximity
                label={slideContent[currentIndex].title}
                className="hero-title font-din-pro"
                fromFontVariationSettings="'wght' 300"
                toFontVariationSettings="'wght' 900"
                containerRef={heroRef}
                radius={100}
                falloff="smooth"
                style={{ 
                  color: 'white',
                  fontSize: 'clamp(2.25rem, 6vw, 5.25rem)',
                  textTransform: 'uppercase',
                  lineHeight: '0.9',
                  letterSpacing: '0.016em',
                  textShadow: '0 2px 20px rgba(0, 0, 0, 0.5)',
                  display: 'block',
                  fontWeight: 300
                }}
              />
            </div>
            <div>
              <VariableProximity
                label={slideContent[currentIndex].subtitle}
                className="hero-subtitle"
                fromFontVariationSettings="'wght' 300, 'opsz' 12"
                toFontVariationSettings="'wght' 700, 'opsz' 24"
                containerRef={heroRef}
                radius={80}
                falloff="smooth"
                style={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: 'clamp(0.9rem, 1.8vw, 1.125rem)',
                  maxWidth: '66.67vw',
                  margin: '0 auto',
                  fontFamily: '"robert-regular", sans-serif',
                  textShadow: '0 1px 10px rgba(0, 0, 0, 0.5)',
                  display: 'block'
                }}
              />
            </div>
          </div>

          {/* Toggle Button (visible when text is hidden) */}
          {!isTextVisible && (
            <button
              className="absolute bottom-8 right-8 z-50 rounded-full bg-white/10 p-3 backdrop-blur-md transition-all duration-300 hover:bg-white/20"
              onClick={toggleTextVisibility}
              title="Show text"
              aria-label="Show text overlay"
            >
              <svg className="size-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          )}

          {/* Navigation Indicators - moved to bottom */}
          <div className="hero-indicators hero-indicators-bottom">
            {[...Array(totalVideos)].map((_, index) => (
              <button
                key={index}
                className={`hero-indicator ${currentIndex === index ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Arrow Navigation (Desktop) - no disabled state for infinite loop */}
          <button 
            className="hero-nav-arrow hero-nav-prev"
            onClick={() => goToSlide(currentIndex === 0 ? totalVideos - 1 : currentIndex - 1)}
            aria-label="Previous slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <button 
            className="hero-nav-arrow hero-nav-next"
            onClick={() => goToSlide(currentIndex === totalVideos - 1 ? 0 : currentIndex + 1)}
            aria-label="Next slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Hero;